import {
  Alert,
  Button,
  Dropzone,
  IconButton,
  PaperclipIcon,
  XCloseIcon,
} from "@minervaproject/fds";
import { KeyboardEvent, useEffect, useRef, useState } from "react";
import { FieldValues, SubmitHandler, useForm } from "react-hook-form";
import { useStore } from "react-redux";

import { createBugReportSubmittedToast } from "components/shared/toasts";
import { useAppDispatch, useAppSelector } from "hooks/redux_hooks";
import { PROFILE_PROPERTIES, useUsersProperties } from "hooks/use_user_properties";
import { sendPubsubMessage } from "shared/reducers/classroom";
import { PubsubMessageTypeType } from "shared/types";
import { classroomSelectors, localSelectors } from "store";
import { BugReporter } from "utils/bug_reporter";

function Attachment({ attachment, handleDelete }: { attachment: File; handleDelete: () => void }) {
  const [showDeleteButton, setShowDeleteButton] = useState(false);

  return (
    <div
      className="flex items-center shadow-level-1 p3 mt3 mr3"
      onMouseEnter={() => setShowDeleteButton(true)}
      onMouseLeave={() => setShowDeleteButton(false)}
      style={{ display: "inline-flex" }}
    >
      <PaperclipIcon width={14} mr={1} />
      <div className="bold mr1" style={{ color: "var(--blue-shade-20)" }}>
        {attachment.name}
      </div>
      <div className="text-black-tint-40">({Math.round(attachment.size / 1000)} kb)</div>
      <IconButton onClick={handleDelete} className={showDeleteButton ? "" : "opacity-0"} ml={1}>
        <XCloseIcon size="lg" color="blackTint70" />
      </IconButton>
    </div>
  );
}

function BugReportForm({ hideModal }: { hideModal: () => void }) {
  const dispatch = useAppDispatch();
  const formRef = useRef<HTMLFormElement>(null);
  const { formState, getValues, handleSubmit, register } = useForm({ mode: "onChange" });
  const currentUserId = useAppSelector(localSelectors.selectCurrentUserId());
  const users = useUsersProperties(PROFILE_PROPERTIES.concat("id", "is_present"));
  const classId = useAppSelector(classroomSelectors.selectClassId());
  const classTitle = useAppSelector(classroomSelectors.selectTitle());
  const [attachments, setAttachments] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const store = useStore();

  const ERROR_TYPES = {
    FILE_TOO_LARGE: "Max file size is 50MB per file.",
    FILE_INVALID_TYPE:
      'You can only upload these file types: ".pdf", ".png" ".jpg", ".jpeg", ".gif", ".bmp", ".mp4"',
    FAILED_UPLOAD_FILES: "Failed to upload attachments. Please try again.",
    FAILED_TO_SUBMIT_REPORT: "Failed to submit bug report. Please try again.",
  };
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    formRef.current?.querySelector("textarea")?.focus();
  }, []);

  const onSubmit: SubmitHandler<FieldValues> = async ({ reportTitle, message }) => {
    setIsSubmitting(true);
    setErrorMessage(null);
    const bugReporter = new BugReporter(
      reportTitle,
      message,
      attachments,
      users,
      currentUserId,
      { id: classId, title: classTitle },
      JSON.stringify(store.getState(), null, 2),
      dispatch,
    );
    // Upload attachments to S3
    try {
      await bugReporter.uploadFiles();
    } catch (error) {
      setErrorMessage(ERROR_TYPES.FAILED_UPLOAD_FILES);
      setIsSubmitting(false);
      return;
    }
    // Report bug to forum and upload to S3
    try {
      await bugReporter.sendBugReport(1);
      await bugReporter.uploadToS3();
    } catch (error) {
      setErrorMessage(ERROR_TYPES.FAILED_TO_SUBMIT_REPORT);
      setIsSubmitting(false);
      return;
    }
    dispatch(
      sendPubsubMessage({
        userId: currentUserId,
        type: PubsubMessageTypeType.UPLOAD_LOGS_TO_S3,
        data: {
          reportTitle,
          message,
          timestamp: bugReporter.timestamp,
        },
      }),
    );
    createBugReportSubmittedToast();
    hideModal();
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter" && event.metaKey && formState.isValid) {
      return handleSubmit(onSubmit(getValues()));
    }
    if (event.key === "Escape") {
      hideModal();
    }
  };

  // TODO: 'any' should be 'FileRejection' from react-dropzone
  const onDropAttachment = (acceptedFiles: File[], fileRejections: any[]) => {
    setErrorMessage(null);
    acceptedFiles.forEach((file) => {
      setAttachments((oldAttachments) => oldAttachments.concat(file));
    });

    if (fileRejections.length) {
      const errorCodes = fileRejections
        .map((fileRejection) => fileRejection.errors)
        .flat()
        .map((error) => error.code);
      if (errorCodes.includes("file-too-large")) setErrorMessage(ERROR_TYPES.FILE_TOO_LARGE);
      if (errorCodes.includes("file-invalid-type")) setErrorMessage(ERROR_TYPES.FILE_INVALID_TYPE);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-full" ref={formRef}>
      <label className="text-black-tint-40 body-s mb1 block" htmlFor="title">
        <span className="bold">Title</span> (Optional)
      </label>
      <input
        id="title"
        type="text"
        className="body-s p3 mb5 w-full border-box"
        style={{ border: "1px solid var(--black-tint-90)", height: "32px" }}
        onKeyDown={onKeyDown}
        {...register("reportTitle", { required: false })}
      />

      <label className="text-black-tint-40 body-s bold mb1 block" htmlFor="description">
        Description
      </label>
      <textarea
        id="description"
        className="w-full p3 body-s mb5 border-box"
        placeholder="Type bug report message here [⌘ + enter]"
        style={{
          borderColor: "var(--black-tint-90)",
          minHeight: "150px",
          resize: "none",
          outline: 0,
        }}
        onKeyDown={onKeyDown}
        {...register("message", { required: true })}
      />

      <div className="body-s">
        <Dropzone
          accept={{
            "application/pdf": [".pdf"],
            "image/png": [".png"],
            "image/jpeg": [".jpg", ".jpeg"],
            "image/gif": [".gif"],
            "image/bmp": [".bmp"],
            "video/mp4": [".mp4"],
          }}
          maxSize={52428800}
          multiple
          noClick
          onDrop={onDropAttachment}
        >
          <p className="text-black-tint-40 body-s my3">
            &quot;.pdf&quot;, &quot;.png&quot;, &quot;.jpg&quot;, &quot;.jpeg&quot;,
            &quot;.gif&quot;, &quot;.bmp&quot;, &quot;.mp4&quot; file types accepted
          </p>
          <p className="text-black-tint-70 body-s">Max File Size: 50MB</p>
        </Dropzone>
      </div>

      <div className="body-s">
        {attachments.map((attachment, index) => (
          <Attachment
            key={attachment.name}
            attachment={attachment}
            handleDelete={() =>
              setAttachments((oldAttachments) =>
                oldAttachments.filter((_, _index) => index !== _index),
              )
            }
          />
        ))}
      </div>

      {errorMessage && (
        <Alert kind="error" showClose={false} mt={5}>
          {errorMessage}
        </Alert>
      )}

      <Button
        type="submit"
        disabled={!formState.isDirty || !formState.isValid || isSubmitting}
        className="mt5"
      >
        Submit
      </Button>
    </form>
  );
}

export { Attachment, BugReportForm };
